"use client";

import { useState, useSyncExternalStore } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Octobre rose : le ruban s'affiche du 1er au 31 octobre (heure de Paris) et
// disparaît seul le 1er novembre. La date se lit côté navigateur, après
// l'hydratation : un en-tête rendu en statique au build ne figerait pas le
// ruban. `?octobre-rose` dans l'adresse l'affiche hors saison, pour le relire.
function isPinkOctober() {
    const month = new Intl.DateTimeFormat("en", {
        timeZone: "Europe/Paris",
        month: "numeric",
    }).format(new Date());

    return month === "10" || window.location.search.includes("octobre-rose");
}

const subscribe = () => () => {};

// Le ruban se dessine d'un seul trait : le pan clair monte de la pointe droite
// jusqu'en haut de la boucle, puis le pan foncé redescend vers la gauche.
// Les deux vitesses se raccordent (easeIn puis easeOut) pour que le trait ne
// marque pas d'arrêt au sommet. Sans animation, le ruban est posé tel quel.
function Ribbon({ className, draw = false }) {
    const reduceMotion = useReducedMotion();
    const animated = draw && !reduceMotion;

    const strand = (delay, ease) =>
        animated
            ? {
                  initial: { pathLength: 0 },
                  animate: { pathLength: 1 },
                  transition: { duration: 0.4, delay, ease },
              }
            : {};

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 32"
            className={className}
            aria-hidden="true"
        >
            <motion.path
                d="M18.5 29.5 L12 18 C9.2 13 8 9.5 8 7 C8 4.2 9.8 2.5 12 2.5"
                fill="none"
                stroke="#F9A8D4"
                strokeWidth="3.6"
                strokeLinejoin="round"
                {...strand(0.15, "easeIn")}
            />
            <motion.path
                d="M12 2.5 C14.2 2.5 16 4.2 16 7 C16 9.5 14.8 13 12 18 L5.5 29.5"
                fill="none"
                stroke="#EC4899"
                strokeWidth="3.6"
                strokeLinejoin="round"
                {...strand(0.55, "easeOut")}
            />
        </svg>
    );
}

// Dans l'en-tête, le tracé ne se joue qu'une fois par session : il accompagne
// la première page vue, pas chaque rechargement. La décision est prise une
// fois par chargement et partagée : plusieurs rubans sont montés en même temps
// (barre latérale et barre mobile masquée), ils doivent répondre pareil.
const DRAWN_KEY = "pink-october-drawn";
let drawDecision = null;

function shouldDrawOnce() {
    if (drawDecision !== null) return drawDecision;
    drawDecision = true;
    try {
        if (window.sessionStorage.getItem(DRAWN_KEY)) drawDecision = false;
        else window.sessionStorage.setItem(DRAWN_KEY, "1");
    } catch {
        // Stockage bloqué (navigation privée) : on dessine quand même.
    }
    return drawDecision;
}

export default function PinkOctober({ className = "" }) {
    const t = useTranslations("layout");
    const visible = useSyncExternalStore(subscribe, isPinkOctober, () => false);
    const [isOpen, setIsOpen] = useState(false);
    const [drawInHeader] = useState(
        () =>
            typeof window !== "undefined" && isPinkOctober() && shouldDrawOnce()
    );

    if (!visible) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label={t("PinkOctober_button")}
                title={t("PinkOctober_button")}
                className={`inline-flex items-center justify-center shrink-0 rounded-md p-1 hover:bg-pink-50 dark:hover:bg-pink-500/10 duration-150 ${className}`}
            >
                <Ribbon className="h-6 w-[18px]" draw={drawInHeader} />
            </button>

            <BottomSheetModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                labelledBy="pink-october-title"
                portal
            >
                <div className="relative p-8">
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        aria-label={t("PinkOctober_close")}
                        className="absolute top-4 right-4 p-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-grayDark-400 dark:hover:text-white dark:hover:bg-bgDark-300 duration-150"
                    >
                        <SolarIcon name="close-circle-linear" className="w-5 h-5" />
                    </button>

                    <Ribbon className="h-12 w-9 mb-4" draw />

                    <h2
                        id="pink-october-title"
                        className="text-xl font-semibold text-gray-900 dark:text-white mb-3"
                    >
                        {t("PinkOctober_title")}
                    </h2>

                    <div className="text-gray-600 dark:text-grayDark-300 text-[15px] leading-relaxed">
                        <p>{t("PinkOctober_intro")}</p>
                        <p>{t("PinkOctober_solopreneurs")}</p>
                        <p>{t("PinkOctober_screening")}</p>
                        <p className="mb-0">{t("PinkOctober_early")}</p>
                    </div>

                    <a
                        href={t("PinkOctober_link_url")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 flex items-center justify-center gap-2 w-full rounded-xl bg-pink-500 hover:bg-pink-600 text-white font-medium py-3 px-4 duration-150"
                    >
                        {t("PinkOctober_link")}
                        <SolarIcon name="arrow-right-up-linear" className="w-4 h-4" />
                    </a>
                </div>
            </BottomSheetModal>
        </>
    );
}
